import { Fragment, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import { ErrorState } from '@/components/feedback/ErrorState';
import { Loading } from '@/components/feedback/Loading';
import { missingAcademicYearMessage, useCurrentAcademicYear } from '@/hooks/useCurrentAcademicYear';
import { YearScopeNotice } from './YearScopeNotice';
import { WorkingYearNoticeShown } from './workingYearNoticeShown';

/**
 * Renders a year-aware page only when there is a working academic year: a
 * loader while the years load, and an error instead of guessing a year when the
 * school has no current year or its current year has ended.
 *
 * A past (closed or ended) working year chosen in the year switcher is noted above the
 * page; notices inside the page then only speak for other years.
 *
 * The children are keyed by the working year: switching the year remounts them on
 * purpose, so filters, pickers and half-filled forms of the old year start over.
 */
export function AcademicYearRequired({ children }: { children: ReactNode }) {
  const workingYear = useCurrentAcademicYear();
  const { isLoading, error, refetch, missing, currentYearId } = workingYear;

  if (isLoading) return <Loading label="Loading academic year…" />;
  if (error && currentYearId === null) {
    return <ErrorState error={error} title="Unable to load academic years" onRetry={refetch} />;
  }
  if (missing || currentYearId === null) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {missingAcademicYearMessage(workingYear)}
      </Alert>
    );
  }
  return (
    <>
      <YearScopeNotice />
      <WorkingYearNoticeShown value>
        <Fragment key={currentYearId}>{children}</Fragment>
      </WorkingYearNoticeShown>
    </>
  );
}
